import { createContext, use, useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { Session } from '@supabase/supabase-js';
import { supabase } from '../../shared/supabase/client';
import { getMyProfile } from '../../shared/queries';
import type { ProfileRow } from '../../shared/queries';

/**
 * Who is signed in, and what the database says they are.
 *
 * Two separate facts, deliberately kept separate:
 *
 * - the **session** comes from Supabase Auth and says only "this browser holds
 *   a valid token for this user id";
 * - the **profile** comes from the database and says which instance they belong
 *   to and whether they are the coach or a client.
 *
 * Nothing is read from the JWT beyond the user id. A claim stamped into a token
 * is stale for up to an hour, so pausing or removing a client would not take
 * effect until it refreshed — unacceptable on a data-protection path
 * (design.md F4). At thirty clients per instance the lookup is noise.
 *
 * A signed-in user with **no** profile row is a real state, not an error: it is
 * a client who has just verified their invitation link and has not been
 * redeemed yet. `WelcomePage` is the only place that state is expected.
 */

export type SessionStatus = 'loading' | 'anonymous' | 'authenticated';

export interface SessionValue {
  status: SessionStatus;
  userId: string | null;
  email: string | null;
  /** Null while loading, for an anonymous visitor, and for a not-yet-redeemed client. */
  profile: ProfileRow | null;
  /** True once the profile lookup for the *current* user has settled. */
  profileLoaded: boolean;
  /** Re-read the profile — after redeeming an invitation, which creates it. */
  refreshProfile: () => Promise<void>;
  signOut: () => Promise<void>;
}

interface ProfileState {
  /** Which user this answer is about, so a stale answer is never shown. */
  forUserId: string | null;
  row: ProfileRow | null;
}

const SessionContext = createContext<SessionValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [sessionLoaded, setSessionLoaded] = useState(false);
  const [profileState, setProfileState] = useState<ProfileState | null>(null);

  useEffect(() => {
    const live = { cancelled: false };

    void supabase.auth.getSession().then(({ data }) => {
      if (live.cancelled) return;
      setSession(data.session);
      setSessionLoaded(true);
    });

    // The callback must not await a Supabase call of its own: supabase-js holds
    // its auth lock while it runs. Setting state is all it does.
    const { data } = supabase.auth.onAuthStateChange((_event, next) => {
      setSession(next);
      setSessionLoaded(true);
    });

    return () => {
      live.cancelled = true;
      data.subscription.unsubscribe();
    };
  }, []);

  const userId = session?.user.id ?? null;

  /**
   * Re-read the profile for **whoever is signed in right now**, not for
   * whoever was signed in when this callback was created.
   *
   * That distinction is the whole point. `WelcomePage` calls this immediately
   * after redeeming an invitation, and at the moment it captured the callback
   * there was no session at all — a version closed over `userId` would write
   * an answer about nobody and leave the app loading forever.
   */
  const refreshProfile = useCallback(async (): Promise<void> => {
    const { data } = await supabase.auth.getSession();
    const currentId = data.session?.user.id ?? null;
    const row = currentId === null ? null : await getMyProfile(supabase, currentId);
    setProfileState({ forUserId: currentId, row });
  }, []);

  useEffect(() => {
    if (!sessionLoaded) return;

    const live = { cancelled: false };
    void (async () => {
      const row = userId === null ? null : await getMyProfile(supabase, userId);
      if (live.cancelled) return;
      setProfileState({ forUserId: userId, row });
    })();

    return () => {
      live.cancelled = true;
    };
  }, [userId, sessionLoaded]);

  const value = useMemo<SessionValue>(() => {
    // Derived rather than stored, so there is no moment where the profile of
    // the previous user is shown as if it belonged to this one.
    const settled = sessionLoaded && profileState !== null && profileState.forUserId === userId;

    return {
      status: !sessionLoaded ? 'loading' : session === null ? 'anonymous' : 'authenticated',
      userId,
      email: session?.user.email ?? null,
      profile: settled ? profileState.row : null,
      profileLoaded: settled,
      refreshProfile,
      signOut: async () => {
        await supabase.auth.signOut();
      },
    };
  }, [session, sessionLoaded, userId, profileState, refreshProfile]);

  return <SessionContext value={value}>{children}</SessionContext>;
}

export function useSession(): SessionValue {
  const value = use(SessionContext);
  if (!value) throw new Error('useSession() was called outside a <SessionProvider>.');
  return value;
}
